import type { Policy, RenewalRow, Task } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { callAction } from "../api";
import { useConfig } from "../components/ConfigProvider";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, EmptyState, ErrorState, LoadingState, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { money, relativeDays, shortDate } from "../theme";
import { DeltaBadge } from "./Today";

export function RenewalsScreen() {
  const t = useT();
  const { locale } = useLocale();
  const { config } = useConfig();
  const qc = useQueryClient();
  const { toast } = useToast();
  const key = ["renewals"];
  const q = useQuery({ queryKey: key, queryFn: async () => (await callAction<"agency.policy.renewals", RenewalRow[]>("agency.policy.renewals", { withinDays: 90 })).result });
  const toggle = useMutation({
    mutationFn: (policyId: string) => callAction<"agency.policy.action", Policy>("agency.policy.action", { policyId, action: "remarket_toggle" }),
    onMutate: async (policyId) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<RenewalRow[]>(key);
      if (prev) qc.setQueryData<RenewalRow[]>(key, prev.map((r) => (r.policy.id === policyId ? { ...r, remarket: !r.remarket, policy: { ...r.policy, remarket: !r.remarket } } : r)));
      return { prev };
    },
    onSuccess: (r) => {
      toast({ title: r.result.remarket ? t("renewals.remarketOn") : t("renewals.remarketOff"), onUndo: () => toggle.mutateAsync(r.result.id) });
      if (config.agency.features.gamification) toast({ title: t("renewals.niceTouch"), durationMs: 2000 });
    },
    onError: (e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast({ title: t("common.error"), body: (e as Error).message, tone: "error" });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ["today"] });
    },
  });
  const followUp = useMutation({
    mutationFn: (r: RenewalRow) =>
      callAction<"agency.task.create", Task>("agency.task.create", {
        customerId: r.customerId,
        policyId: r.policy.id,
        title: `${t("renewals.followUp")}: ${r.policy.carrier} ${r.policy.line}`,
        dueAt: new Date(Date.now() + 2 * 86_400_000).toISOString(),
        kind: "renewal",
      }),
    onSuccess: () => {
      toast({ title: t("task.created"), tone: "success" });
      qc.invalidateQueries({ queryKey: ["today"] });
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });
  const userName = (id?: string) => config.users.find((u) => u.id === id)?.name.split(" ")[0] ?? "—";

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold">{t("renewals.title")}</h1>
        <p className="text-ink/60">{t("renewals.subtitle")}</p>
      </div>
      {q.isLoading ? <LoadingState /> : null}
      {q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : null}
      {q.data && q.data.length === 0 ? <EmptyState title={t("renewals.none")} /> : null}
      {q.data && q.data.length > 0 ? (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-ink/55">
              <tr>
                <th className="px-4 py-3">{t("common.name")}</th>
                <th className="px-4 py-3 hidden sm:table-cell">{t("common.carrier")}</th>
                <th className="px-4 py-3">{t("renewals.daysLeft")}</th>
                <th className="px-4 py-3">{t("common.premium")}</th>
                <th className="px-4 py-3">{t("renewals.delta")}</th>
                <th className="px-4 py-3">{t("renewals.remarket")}</th>
                <th className="px-4 py-3 hidden md:table-cell">{t("common.owner")}</th>
                <th className="px-4 py-3">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {[...q.data].sort((a, b) => a.daysUntil - b.daysUntil).map((r) => (
                <tr key={r.policy.id} className={cn("border-t border-ink/5", r.daysUntil <= 7 && "bg-accent/8")}>
                  <td className="px-4 py-2">
                    <Link to={`/customers/${r.customerId}`} className="font-semibold hover:underline block min-h-[36px] leading-[36px]">{r.customerName}</Link>
                    <span className="sm:hidden text-xs text-ink/55">{r.policy.carrier} · {r.policy.line}</span>
                  </td>
                  <td className="px-4 py-2 hidden sm:table-cell">{r.policy.carrier} · {r.policy.line}</td>
                  <td className="px-4 py-2">
                    <Badge tone={r.daysUntil < 0 ? "red" : r.daysUntil <= 14 ? "amber" : "neutral"}>{relativeDays(r.daysUntil, locale)}</Badge>
                    <span className="block text-xs text-ink/50">{shortDate(r.policy.expirationDate, locale)}</span>
                  </td>
                  <td className="px-4 py-2">{money(r.policy.premiumCents)}</td>
                  <td className="px-4 py-2"><DeltaBadge pct={r.premiumDeltaPct} cents={r.premiumDeltaCents} /></td>
                  <td className="px-4 py-2">
                    <button role="switch" aria-checked={r.remarket} onClick={() => toggle.mutate(r.policy.id)} className={cn("relative h-8 w-14 rounded-full transition-colors", r.remarket ? "bg-accent" : "bg-ink/15")} aria-label={t("renewals.remarket")}>
                      <span className={cn("absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-all", r.remarket ? "left-7" : "left-1")} />
                    </button>
                  </td>
                  <td className="px-4 py-2 hidden md:table-cell">{userName(r.ownerUserId)}</td>
                  <td className="px-4 py-2">
                    <Button variant="outline" size="sm" onClick={() => followUp.mutate(r)}><CalendarPlus className="h-4 w-4" />{t("renewals.followUp")}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}
    </div>
  );
}
